var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTranslation } from 'react-i18next';
import { usePackageManagement } from '../../../../hooks/packing';
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Tag, Tooltip, Avatar } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { selectPackagePackerBySeqId } from '../../../../redux/packing';
import { packingTagBadgeClassName } from '../../shared/badgeStyles';
var getNameInitials = function (name) {
    var normalizedName = name.trim();
    if (!normalizedName)
        return '?';
    var parts = normalizedName.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    var firstInitial = parts[0].charAt(0);
    var lastInitial = parts[parts.length - 1].charAt(0);
    return ("" + firstInitial + lastInitial).toUpperCase();
};
export var PackageItem = function (_a) {
    var item = _a.item, isActive = _a.isActive, createdByLabel = _a.createdByLabel, dataPackageKey = _a.dataPackageKey, onSelect = _a.onSelect;
    var t = useTranslation().t;
    var getBoxTypeName = usePackageManagement().getBoxTypeName;
    var boxTypeDescription = useMemo(function () {
        return (item.boxTypeDescription ||
            (item.shipmentBoxTypeId ? getBoxTypeName(item.shipmentBoxTypeId) : ''));
    }, [item, getBoxTypeName]);
    var creatorInitials = useMemo(function () { return getNameInitials(createdByLabel); }, [createdByLabel]);
    return (_jsxs("button", __assign({ type: 'button', "data-package-key": dataPackageKey, className: "text-left px-3 py-2.5 rounded-lg transition-all " + (isActive
            ? 'bg-blue-50 border-2 border-primary shadow-sm'
            : 'bg-white hover:bg-blue-50 border border-gray-200'), onClick: function () { return onSelect(item); } }, { children: [_jsxs("div", __assign({ className: 'flex items-center justify-between' }, { children: [_jsx("span", __assign({ className: "font-bold text-sm " + (isActive ? 'text-primary' : 'text-gray-700') }, { children: item.displayId }), void 0), _jsxs("div", __assign({ className: 'flex items-center gap-1' }, { children: [item.hasSerializedItems && (_jsx(Tag, __assign({ color: 'purple', className: packingTagBadgeClassName }, { children: t('S/N') }), void 0)), _jsx(Tag, __assign({ color: item.kind === 'virtual' ? 'orange' : item.isClosed ? 'green' : 'blue', className: packingTagBadgeClassName }, { children: item.kind === 'virtual' ? t('Pending') : item.isClosed ? t('Closed') : t('Open') }), void 0)] }), void 0)] }), void 0), boxTypeDescription ? (_jsx("div", __assign({ className: 'text-xs text-gray-500 mt-1 truncate' }, { children: boxTypeDescription || 'N/A' }), void 0)) : (_jsx("div", __assign({ className: 'text-xs text-gray-400 mt-1 truncate' }, { children: t('Unset box type') }), void 0)), _jsxs("div", __assign({ className: 'mt-1.5 flex items-center justify-between gap-2' }, { children: [_jsx("span", __assign({ className: 'text-secondary-text text-xs' }, { children: item.itemCount > 0 ? item.itemCount + " " + t('items') : 'Empty' }), void 0), _jsx(Tooltip, __assign({ title: t('Created by') + ": " + createdByLabel }, { children: _jsx(Avatar, __assign({ size: 24, style: {
                                backgroundColor: '#f3f4f6',
                                color: '#6b7280',
                                fontSize: 10,
                                fontWeight: 700
                            } }, { children: creatorInitials }), void 0) }), void 0)] }), void 0)] }), void 0));
};
export var PackageList = function (_a) {
    var items = _a.items, selectedPackageKey = _a.selectedPackageKey, onSelect = _a.onSelect;
    var t = useTranslation().t;
    var packagePackerBySeqId = useSelector(selectPackagePackerBySeqId);
    var listScrollContainerRef = useRef(null);
    var _b = useState('all'), packageOwnershipFilter = _b[0], setPackageOwnershipFilter = _b[1];
    var _c = useState('all'), packageStatusFilter = _c[0], setPackageStatusFilter = _c[1];
    var _d = useState(''), searchTerm = _d[0], setSearchTerm = _d[1];
    var hasSearchTerm = searchTerm.trim().length > 0;
    var currentUserPartyId = useMemo(function () {
        return String(localStorage.getItem('userPartyId') || '')
            .trim()
            .toLowerCase();
    }, []);
    var currentUserName = useMemo(function () { return String(localStorage.getItem('loggedInUserName') || '').trim(); }, []);
    var ownershipFilteredItems = useMemo(function () {
        if (packageOwnershipFilter === 'all') {
            return items;
        }
        if (!currentUserPartyId) {
            return [];
        }
        return items.filter(function (pkg) {
            var _a;
            if (pkg.kind === 'virtual') {
                return true;
            }
            if (!pkg.shipmentPackageSeqId) {
                return false;
            }
            var packerPartyId = String(((_a = packagePackerBySeqId === null || packagePackerBySeqId === void 0 ? void 0 : packagePackerBySeqId[pkg.shipmentPackageSeqId]) === null || _a === void 0 ? void 0 : _a.packerPartyId) || '')
                .trim()
                .toLowerCase();
            return packerPartyId === currentUserPartyId;
        });
    }, [currentUserPartyId, items, packageOwnershipFilter, packagePackerBySeqId]);
    var statusFilteredItems = useMemo(function () {
        if (packageStatusFilter === 'all') {
            return ownershipFilteredItems;
        }
        return ownershipFilteredItems.filter(function (pkg) {
            if (pkg.kind === 'virtual')
                return false;
            return packageStatusFilter === 'closed' ? pkg.isClosed : !pkg.isClosed;
        });
    }, [ownershipFilteredItems, packageStatusFilter]);
    var filteredItems = useMemo(function () {
        if (!searchTerm.trim())
            return statusFilteredItems;
        var term = searchTerm.toLowerCase();
        return statusFilteredItems.filter(function (pkg) {
            return pkg.displayId.toLowerCase().includes(term) ||
                (pkg.shipmentPackageSeqId || '').toLowerCase().includes(term) ||
                (pkg.boxTypeDescription || '').toLowerCase().includes(term);
        });
    }, [statusFilteredItems, searchTerm]);
    var selectedFilteredItemIndex = useMemo(function () {
        return selectedPackageKey ? filteredItems.findIndex(function (item) { return item.key === selectedPackageKey; }) : -1;
    }, [filteredItems, selectedPackageKey]);
    var getCreatedByLabel = useCallback(function (pkg) {
        if (pkg.kind === 'virtual') {
            return currentUserName || t('You');
        }
        if (!pkg.shipmentPackageSeqId) {
            return t('Unknown');
        }
        var packer = packagePackerBySeqId === null || packagePackerBySeqId === void 0 ? void 0 : packagePackerBySeqId[pkg.shipmentPackageSeqId];
        var packerName = String((packer === null || packer === void 0 ? void 0 : packer.packerName) || '').trim();
        if (packerName) {
            return packerName;
        }
        var packerPartyId = String((packer === null || packer === void 0 ? void 0 : packer.packerPartyId) || '').trim();
        if (packerPartyId) {
            return packerPartyId;
        }
        return t('Unknown');
    }, [currentUserName, packagePackerBySeqId, t]);
    useEffect(function () {
        var _a;
        if (!selectedPackageKey || selectedFilteredItemIndex < 0)
            return;
        var selectedItemElement = (_a = listScrollContainerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("[data-package-key=\"" + selectedPackageKey + "\"]");
        if (!selectedItemElement)
            return;
        selectedItemElement.scrollIntoView({ block: 'nearest' });
    }, [selectedFilteredItemIndex, selectedPackageKey]);
    return (_jsxs("div", __assign({ className: 'flex h-full min-h-0 flex-col' }, { children: [_jsxs("div", __assign({ className: 'grid grid-cols-2 rounded-t-xl p-2 border border-gray-2 bg-gray-50 p-1 ' }, { children: [_jsx("button", __assign({ type: 'button', className: "rounded-md px-3 py-2 text-xs font-semibold transition-colors " + (packageOwnershipFilter === 'all'
                            ? 'bg-white text-primary shadow-sm border border-gray-200'
                            : 'text-gray-500 hover:text-gray-700'), onClick: function () { return setPackageOwnershipFilter('all'); } }, { children: t('All Packages') }), void 0), _jsx("button", __assign({ type: 'button', className: "rounded-md px-3 py-2 text-xs font-semibold transition-colors " + (packageOwnershipFilter === 'my'
                            ? 'bg-white text-primary shadow-sm border border-gray-200'
                            : 'text-gray-500 hover:text-gray-700'), onClick: function () { return setPackageOwnershipFilter('my'); } }, { children: t('My Packages') }), void 0)] }), void 0), _jsx("div", __assign({ className: 'border-b border-gray-200 p-3' }, { children: _jsxs("div", __assign({ className: 'flex items-center gap-2' }, { children: [_jsxs("div", __assign({ className: 'relative flex-1' }, { children: [_jsx(SearchOutlined, { className: 'pointer-events-none absolute left-3 text-gray-400 self-center' }, void 0), _jsx("input", { type: 'text', value: searchTerm, onChange: function (event) { return setSearchTerm(event.target.value); }, placeholder: t('Search...'), className: 'w-full rounded-lg border border-gray-300 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none' }, void 0)] }), void 0), _jsxs("select", __assign({ value: packageStatusFilter, onChange: function (event) { return setPackageStatusFilter(event.target.value); }, className: 'h-9 w-24 rounded-lg border border-gray-300 bg-gray-50 px-2 text-xs font-medium text-gray-600 outline-none focus:border-primary' }, { children: [_jsx("option", __assign({ value: 'all' }, { children: t('All') }), void 0), _jsx("option", __assign({ value: 'open' }, { children: t('Open') }), void 0), _jsx("option", __assign({ value: 'closed' }, { children: t('Closed') }), void 0)] }), void 0)] }), void 0) }), void 0), _jsx("div", __assign({ ref: listScrollContainerRef, className: 'flex-1 overflow-y-auto custom-scrollbar' }, { children: _jsx("div", __assign({ className: 'flex flex-col gap-1.5 p-2' }, { children: filteredItems.length === 0 ? (_jsx("div", __assign({ className: 'text-center text-gray-400 text-sm py-6' }, { children: hasSearchTerm ? t('No packages matching your search term') : t('No packages yet') }), void 0)) : (filteredItems.map(function (item) { return (_jsx(PackageItem, { dataPackageKey: item.key, item: item, isActive: item.key === selectedPackageKey, createdByLabel: getCreatedByLabel(item), onSelect: onSelect }, item.key)); })) }), void 0) }), void 0)] }), void 0));
};
